import { useState, useCallback, useMemo, useRef } from 'react'
import { useFilterStore } from '../stores/useFilterStore'
import { useGraphStore } from '../stores/useGraphStore'
import { useZustand } from './useZustand'
import {
    useContextsStore,
    findContextByName,
} from '../stores/useContextsStore'

const useGraphSelection = () => {
    // Selected statement IDs come from global filter store
    const selectedStatementIds = useFilterStore(
        state => state.selectedStatements
    )
    const resetSelectedStatements = useFilterStore(
        state => state.resetSelectedStatements
    )
    const filteredStatements = useFilterStore(state => state.filteredStatements)
    const isAnyFilterActive = useFilterStore(state => state.isAnyFilterActive())
    const isAnyHighlightActive = useFilterStore(state =>
        state.isAnyHighlightActive()
    )
    const isAnyRecalculateActive = useFilterStore(state =>
        state.isAnyRecalculateActive()
    )
    const isOnlyTopicHighlightActive = useFilterStore(state =>
        state.isOnlyTopicHighlightActive()
    )

    const [statementSelectedLabels, setStatementSelectedLabels] = useState([])
    const [selectedTopics, setSelectedTopics] = useState([])

    const pinnedNodes = useFilterStore(state => state.pinnedNodes)
    const highlight = useFilterStore(state => state.highlight)
    const setPinnedNodes = useFilterStore(state => state.setPinnedNodes)
    const setSelectedContextId = useFilterStore(
        state => state.setSelectedContextId
    )

    // Convert pinnedNodes array to Set for compatibility with existing logic
    const graphSelectedNodes = useMemo(() => new Set(pinnedNodes), [
        pinnedNodes,
    ])

    const selectedGroups = useMemo(
        () =>
            highlight.topics.length > 0
                ? highlight.topics.map(({ id }) => id.toString())
                : [],
        [highlight.topics]
    )

    // The graph package fires `onSelectedNodes(selectedWords)` on mount with
    // its internal state ([]) before its `useSelectedNodes` hook imports our
    // `selectedNodes` prop (which is delayed by a layer-switch setTimeout).
    // Without this guard, a URL-seeded selection (?keywords=) gets wiped by
    // that initial empty emission, then briefly restored, then wiped again
    // when the empty prop propagates back through the graph's hook.
    const initEmptyGuardRef = useRef(true)

    const onSelectedNodes = useCallback(
        nodes => {
            if (initEmptyGuardRef.current) {
                initEmptyGuardRef.current = false
                if (nodes.length === 0 && pinnedNodes.length > 0) {
                    return
                }
            }

            // Separate context nodes from concept nodes
            const incomingContexts = nodes.filter(n =>
                n.startsWith('context: ')
            )
            const incomingConcepts = nodes.filter(
                n => !n.startsWith('context: ')
            )

            // Diff against previous selection to find what changed
            const prevContexts = pinnedNodes.filter(n =>
                n.startsWith('context: ')
            )
            const prevConcepts = pinnedNodes.filter(
                n => !n.startsWith('context: ')
            )

            const addedContexts = incomingContexts.filter(
                n => !prevContexts.includes(n)
            )
            const removedContexts = prevContexts.filter(
                n => !incomingContexts.includes(n)
            )

            // Update context filter based on what changed
            if (addedContexts.length > 0) {
                const ctxName = addedContexts[0].replace('context: ', '')
                const entry = findContextByName(
                    useContextsStore.getState(),
                    ctxName
                )
                if (entry) {
                    setSelectedContextId(entry.id)
                }
            } else if (
                removedContexts.length > 0 &&
                incomingContexts.length === 0
            ) {
                // All context nodes deselected — clear filter
                setSelectedContextId(null)
            }

            // Build the new pinnedNodes list.
            // Context nodes keep spaces (graph node IDs use spaces).
            // Concept nodes replace spaces with underscores (hashtag matching).
            const newPinned = [
                ...incomingContexts,
                ...incomingConcepts.map(n => n.replace(/ /g, '_')),
            ]

            const hasSameValues =
                newPinned.length === pinnedNodes.length &&
                newPinned.every(n => pinnedNodes.includes(n))

            if (hasSameValues) {
                return
            }

            setPinnedNodes(newPinned)
            setStatementSelectedLabels([])
            resetSelectedStatements()
        },
        [
            pinnedNodes,
            setPinnedNodes,
            resetSelectedStatements,
            setSelectedContextId,
        ]
    )

    const onTopicSelect = topics => {
        setPinnedNodes([])
        setStatementSelectedLabels([]) // Clear statement selections
        resetSelectedStatements()
        setSelectedTopics(topics.map(({ id }) => id))
    }

    const currentNodeSelection = useMemo(() => {
        if (pinnedNodes.length > 0) {
            return pinnedNodes
        }
        return Array.from(graphSelectedNodes)
    }, [pinnedNodes, graphSelectedNodes, filteredStatements])

    // Get edges from the graph store
    const graphStoreEdges = useGraphStore(
        state =>
            state.graphData?.entriesAndGraphOfContext?.graph?.graphologyGraph
                ?.edges
    )
    const isRecalculatedGraph = useGraphStore(
        state => state.isRecalculatedGraph
    )

    // Compute highlighted and removed edge IDs from current filtered statements
    const { highlightedEdges, filteredEdges, removedEdges } = useMemo(() => {
        try {
            const edges = graphStoreEdges || []

            // No graph yet
            if (edges.length === 0)
                return {
                    highlightedEdges: null,
                    filteredEdges: [],
                    removedEdges: [],
                }

            // If there are no filtered statements, everything is visible and nothing is removed
            if (!filteredStatements || filteredStatements.length === 0) {
                return {
                    highlightedEdges: null,
                    filteredEdges: [],
                    removedEdges: [],
                }
            }

            const entryIdSet = new Set(
                filteredStatements.map(s => String(s.uid))
            )

            const entryIdSetHighlighted = new Set(
                selectedStatementIds.size === 0
                    ? filteredStatements
                          .filter(s => !s.grayedOut)
                          .map(s => String(s.uid))
                    : Array.from(selectedStatementIds).map(id => String(id))
            )

            // If no filters are active and no statements are selected,
            // nothing needs to be filtered/highlighted
            if (!isAnyFilterActive && selectedStatementIds.size === 0) {
                return {
                    highlightedEdges: null,
                    filteredEdges: null,
                    removedEdges: [],
                }
            }

            const filteredEdgesMap = new Map()
            const highlightedEdgesMap = new Map()
            const allEdgesMap = new Map()

            edges.forEach(e => {
                // Graphology edge structure: { key, source, target, attributes: { context_matrix, ... } }
                const edgeId = e.key
                if (edgeId === undefined) return

                const edgeIdStr = String(edgeId)
                const edgeObj = {
                    id: edgeIdStr,
                    source: e.source,
                    target: e.target,
                    sourceName: e.source,
                    targetName: e.target,
                    weight: e.attributes?.weight,
                }

                // Store all edges
                allEdgesMap.set(edgeIdStr, edgeObj)

                const contextMatrix = e.attributes?.context_matrix
                if (!contextMatrix) return

                for (const context in contextMatrix) {
                    const statementMap = contextMatrix[context]
                    for (const statementId in statementMap) {
                        // edge belongs to any of filtered statements
                        if (entryIdSet.has(statementId)) {
                            filteredEdgesMap.set(edgeIdStr, edgeObj)
                        }

                        // edge belongs to any of highlighted statements or selected statements
                        if (entryIdSetHighlighted.has(statementId)) {
                            highlightedEdgesMap.set(edgeIdStr, edgeObj)
                        }
                    }
                }
            })

            const highlightedEdges = Array.from(highlightedEdgesMap.values())
            const filteredEdges = Array.from(filteredEdgesMap.values())

            // Calculate removed edges: allEdges - filteredEdges
            const filteredEdgeIds = new Set(filteredEdgesMap.keys())
            const removedEdges = Array.from(allEdgesMap.values()).filter(
                edge => !filteredEdgeIds.has(edge.id)
            )

            // If only topic highlight is active, don't calculate highlightedEdges
            // (graph uses selectedTopics instead for topic highlighting)
            if (isOnlyTopicHighlightActive) {
                return {
                    highlightedEdges: null,
                    // Still provide filteredEdges if recalculate filter is active
                    filteredEdges: isAnyRecalculateActive
                        ? filteredEdges
                        : null,
                    removedEdges:
                        isAnyRecalculateActive && !isRecalculatedGraph
                            ? removedEdges
                            : [],
                }
            }

            // If no recalculate filter is active but highlight is active,
            // only provide highlightedEdges (no filtering needed)
            if (!isAnyRecalculateActive && isAnyHighlightActive) {
                return {
                    highlightedEdges,
                    filteredEdges: null,
                    removedEdges: [],
                }
            }
            // TODO: revisit under OR pin semantics. With many pins covering
            // most of the graph, this short-circuit can flip highlight off
            // entirely — making the graph silently disagree with the panel.
            const isNotHighlighted = highlightedEdges.length === edges.length
            if (isNotHighlighted) {
                return {
                    highlightedEdges: null,
                    filteredEdges: null,
                    removedEdges: [],
                }
            }

            if (filteredEdges.length === edges.length) {
                return {
                    highlightedEdges: !isNotHighlighted && highlightedEdges,
                    filteredEdges: null,
                    removedEdges: [],
                }
            }

            if (highlightedEdges.length === 0 || !isAnyHighlightActive) {
                return {
                    highlightedEdges: null,
                    filteredEdges,
                    removedEdges: isRecalculatedGraph ? [] : removedEdges,
                }
            }

            return {
                highlightedEdges,
                filteredEdges,
                removedEdges: isRecalculatedGraph ? [] : removedEdges,
            }
        } catch (e) {
            return {
                highlightedEdges: null,
                filteredEdges: null,
                removedEdges: null,
            }
        }
    }, [
        isRecalculatedGraph,
        filteredStatements,
        selectedStatementIds,
        isAnyFilterActive,
        isAnyHighlightActive,
        isAnyRecalculateActive,
        isOnlyTopicHighlightActive,
    ])
    return {
        graphSelectedNodes,
        statementSelectedLabels,
        selectedStatementIds,
        currentNodeSelection,
        highlightedEdges,
        filteredEdges,
        removedEdges,
        onSelectedNodes,
        onTopicSelect,
        selectedTopics: selectedGroups,
    }
}

export default useGraphSelection
